import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DashboardService } from '../../../services/dashboard/dashboard.service';
import { finalize } from 'rxjs/operators';

export interface SaveOrgTemplateDialogData {
  mode?: 'create' | 'edit';
  defaultName: string;
  description?: string;
  targetRole?: string | null;
  targetRoles?: string[];
  dashboardId: string;
  templateId?: string;
  widgets?: unknown[];
  allowPlatformTemplate?: boolean;
}

function initialTargetRoles(data: SaveOrgTemplateDialogData): string[] {
  if (data.targetRoles?.length) {
    return [...data.targetRoles];
  }
  if (data.targetRole) {
    return [data.targetRole];
  }
  return [];
}

@Component({
  selector: 'app-save-org-template-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <h2 mat-dialog-title>{{ dialogTitle }}</h2>
    <mat-dialog-content class="dialog-content">
      <form [formGroup]="form" class="form">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Navn</mat-label>
          <input matInput formControlName="name" />
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Beskrivelse</mat-label>
          <textarea matInput rows="3" formControlName="description"></textarea>
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Målroller</mat-label>
          <mat-select formControlName="targetRoles" multiple>
            <mat-option value="orgadmin">Organisationsadministrator</mat-option>
            <mat-option value="uddansvarlig">Uddannelsesansvarlig</mat-option>
            <mat-option value="orgbruger">Organisationsbruger</mat-option>
          </mat-select>
          <mat-hint>Ingen valgt = alle roller kan bruge skabelonen som rolle-fallback.</mat-hint>
        </mat-form-field>
        @if (isCreateMode && data.allowPlatformTemplate) {
        <mat-checkbox formControlName="isDunaPlatform">
          DUNA platformsskabelon (alle organisationer)
        </mat-checkbox>
        }
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end" class="dialog-actions">
      <button mat-button mat-dialog-close>Annuller</button>
      <button
        mat-raised-button
        color="primary"
        [disabled]="form.invalid || saving"
        (click)="save()"
      >
        Gem
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .dialog-content {
        color: var(--mat-sys-on-surface, inherit);
        padding: 8px 24px 4px;
      }
      .form {
        display: flex;
        flex-direction: column;
        gap: 16px;
        min-width: min(420px, 92vw);
        padding-top: 4px;
      }
      .full-width {
        width: 100%;
      }
      .dialog-actions {
        padding: 12px 16px 20px;
        gap: 8px;
      }
    `,
  ],
})
export class SaveOrgTemplateDialogComponent {
  form = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    description: [''],
    targetRoles: [[] as string[]],
    isDunaPlatform: [false],
  });
  saving = false;

  get isCreateMode(): boolean {
    return (this.data.mode ?? 'create') === 'create';
  }

  get dialogTitle(): string {
    return this.isCreateMode
      ? 'Gem aktuelt dashboard som skabelon'
      : 'Rediger skabelon';
  }

  constructor(
    private fb: FormBuilder,
    private dashboardService: DashboardService,
    private dialogRef: MatDialogRef<SaveOrgTemplateDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: SaveOrgTemplateDialogData
  ) {
    this.form.patchValue({
      name: data.defaultName || '',
      description: data.description ?? '',
      targetRoles: initialTargetRoles(data),
      isDunaPlatform: false,
    });
  }

  save(): void {
    if (this.form.invalid) {
      return;
    }
    const { name, description, targetRoles, isDunaPlatform } =
      this.form.getRawValue();
    const roles = targetRoles ?? [];
    this.saving = true;

    if (this.isCreateMode) {
      const widgets = this.data.widgets ?? [];
      if (!widgets.length) {
        this.saving = false;
        this.dialogRef.close(false);
        return;
      }
      this.dashboardService
        .saveDashboardAsTemplate(
          this.data.dashboardId,
          name!,
          description || '',
          !!isDunaPlatform,
          roles.length === 1 ? roles[0] : null,
          widgets,
          roles
        )
        .pipe(finalize(() => (this.saving = false)))
        .subscribe({
          next: () => this.dialogRef.close(true),
          error: () => this.dialogRef.close(false),
        });
      return;
    }

    const templateId = this.data.templateId;
    if (!templateId) {
      this.saving = false;
      this.dialogRef.close(false);
      return;
    }

    this.dashboardService
      .updateDashboardTemplate(templateId, {
        name: name!.trim(),
        description: description || '',
        targetRoles: roles,
      })
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: () => this.dialogRef.close(false),
      });
  }
}
